import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react";
import { Segmented, Skeleton } from "antd";
import { Button } from "@/shared/components/button";
import { EmptyState } from "@/shared/components/EmptyState";
import { FilePane } from "@/features/patient/records/components/OriginalFileTab";
import { isAbnormalLabStatus } from "@/features/patient/diagnostics/utils/adaptDiagnostics";
import type {
  LabReportRecord as LabResult,
  LabComponentResult as LabTestResult,
} from "@/features/patient/diagnostics/utils/adaptDiagnostics";
import { useImagingStudies } from "@/portals/patient/context/ImagingStudiesContext";
import type { ImagingSeries } from "@/portals/patient/api/imagingStudiesApi";
import { ImagingStudyList } from "@/features/patient/diagnostics/components/imaging/ImagingStudyList";
import { cn } from "@/shared/lib/utils";

interface DiagnosticsImagingSectionProps {
  labResults: LabResult[];
  isLoading?: boolean;
}

type Tab = "labs" | "imaging";

function formatDate(dateString: string): string {
  if (!dateString) return "Not available";
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const date = new Date(dateString + "T00:00:00");
  if (Number.isNaN(date.getTime())) return "Not available";
  return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
}

const categoryLabels: Record<string, string> = {
  blood: "Blood Test", urine: "Urine Test", microbiology: "Microbiology",
  pathology: "Pathology", genetic: "Genetic Test", other: "Other",
};

// Design reference: the status word (Low/High/Normal) always renders in the
// same navy as the component name — only the big value and its dot switch to
// coral (abnormal) or blue (normal). A colored label was the old behavior;
// keeping it navy for every status matches the mockup exactly.
function getResultStatusMeta(status: LabTestResult["status"]) {
  switch (status) {
    case "low":
    case "high":
      return { label: status === "low" ? "Low" : "High", valueClass: "text-[#E4002B]/50", dotClass: "bg-[#E4002B]/50" };
    case "critical_low":
    case "critical_high":
      return { label: "Critical", valueClass: "text-[#DC2626]", dotClass: "bg-[#DC2626]" };
    case "unknown":
      return { label: "Not Assessed", valueClass: "text-[#9CA3AF]", dotClass: "bg-[#9CA3AF]" };
    default:
      return { label: "Normal", valueClass: "text-[#111827]", dotClass: "bg-[#00A7F2]" };
  }
}

const greyPillClass = "rounded-md bg-[#F3F4F6] px-2 py-0.5 text-[10px] font-semibold text-[#6B7280]";
// !min-w-0 is load-bearing: without it, a long test/study name below has no
// upper bound and forces this button (and the single-column grid track it
// sits in) wider than its 340px sidebar column, overflowing into — and
// visually painting over — the detail panel next to it.
// !items-stretch overrides antd's own Button default of `align-items: center`
// on this flex-col container. Without it, each stacked row (title, pills,
// date) sizes to its own shrink-to-fit content width and gets CENTERED
// instead of stretched to the button's width — a row wider than the button
// then overflows equally on both sides, floating outside the card entirely.
const rowBaseClass = "!flex !h-auto !w-full !min-w-0 !flex-col !items-stretch !gap-2 !rounded-2xl !border !bg-white !p-4 !text-left !shadow-sm hover:!bg-[#FFF8F8] hover:!shadow-md";
const filterPillClass = "!h-auto !rounded-full !border !px-4 !py-1.5 !text-xs !font-bold";
const filterPillActiveClass = "!border-[#E4002B]/50 !bg-[#E4002B]/50 !text-white hover:!border-[#E4002B]/50 hover:!bg-[#E4002B]/50 hover:!text-white";
const filterPillInactiveClass = "!border-[#E5E7EB] !bg-white !text-[#374151] hover:!border-[#D1D5DB]";

function StatCard({
  icon, label, value, onClick,
}: {
  icon: string; label: string; value: number; onClick: () => void;
}) {
  return (
    <Button
      variant="outline"
      onClick={onClick}
      className="!flex !h-auto !min-h-[110px] !flex-col !items-stretch !justify-between !rounded-3xl !border-[#E5E7EB] !bg-white !px-6 !py-5 !text-left !shadow-sm hover:!-translate-y-0.5 hover:!border-[#E5E7EB] hover:!bg-white hover:!shadow-lg"
    >
      <div className="flex items-center gap-2">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#E4002B]/50">
          <Icon icon={icon} className="h-4 w-4 !text-white" />
        </div>
        <p className="text-sm font-semibold text-[#111827]">{label}</p>
      </div>
      <p className="text-right text-3xl font-bold text-[#111827]">{value}</p>
    </Button>
  );
}

function EmptyListState({ icon, message }: { icon: string; message: string }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-[#E5E7EB] bg-white py-10 text-center">
      <Icon icon={icon} className="h-10 w-10 text-[#9CA3AF]" />
      <p className="mt-3 text-xs font-semibold text-[#9CA3AF]">{message}</p>
    </div>
  );
}

function LabRow({
  result, isSelected, onSelect,
}: {
  result: LabResult; isSelected: boolean; onSelect: () => void;
}) {
  const abnormalCount = result.results.filter(r => isAbnormalLabStatus(r.status)).length;
  // Every component genuinely unassessed (no interpretation, nothing to
  // compare against a range) — distinct from "checked and found normal".
  const allUnassessed = result.results.length > 0 && result.results.every(r => r.status === "unknown");

  return (
    <Button
      variant="outline"
      onClick={onSelect}
      className={cn(rowBaseClass, isSelected ? "!border-2 !border-[#E4002B]/50 !bg-white" : "!border-[#E5E7EB] !bg-white")}
    >
      <div className="flex min-w-0 items-start justify-between gap-2">
        <div className="flex min-w-0 items-start gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#E4002B]/50">
            <Icon icon="lucide:test-tube" className="h-4 w-4 !text-white" />
          </div>
          <p className="min-w-0 truncate text-sm font-extrabold text-[#0A2947]">{result.testName}</p>
        </div>
        <span className={cn(
          "shrink-0 rounded-full px-2.5 py-0.5 text-[10px] font-bold",
          abnormalCount > 0
            ? "bg-[#E4002B]/50 text-white"
            : allUnassessed ? "bg-[#F3F4F6] text-[#6B7280]" : "bg-[#DCFCE7] text-[#16A34A]"
        )}>
          {abnormalCount > 0 ? `${abnormalCount} Abnormal` : allUnassessed ? "Not Assessed" : "Normal"}
        </span>
      </div>
      <div className="flex flex-wrap gap-1.5 pl-12">
        <span className={greyPillClass}>{categoryLabels[result.category]}</span>
        <span className={cn(greyPillClass, "capitalize")}>{result.status}</span>
      </div>
      <p className="pl-12 text-xs text-[#9CA3AF]">
        {formatDate(result.collectionDate)} · {result.performingLab || "Lab not recorded"}
      </p>
    </Button>
  );
}

function LabResultDetail({ result }: { result: LabResult }) {
  return (
    <div className="rounded-3xl border border-[#E5E7EB] bg-white p-6 shadow-sm">
      <div className="mb-1 flex items-center gap-2">
        <span className="text-xs font-extrabold uppercase tracking-wide text-[#E4002B]">Lab Result</span>
        <span className="rounded-full bg-[#F3F4F6] px-2.5 py-0.5 text-[10px] font-bold capitalize text-[#6B7280]">{result.status}</span>
      </div>
      <h2 className="text-xl font-extrabold text-[#111827]">{result.testName}</h2>
      <p className="mt-1 text-sm text-[#6B7280]">
        {formatDate(result.collectionDate)} <span className="mx-1">|</span> Ordered by {result.orderingProvider || "provider not recorded"} <span className="mx-1">|</span> {result.performingLab || "lab not recorded"}
      </p>

      {result.results.length === 0 && (
        <EmptyState
          className="mt-6"
          icon={<Icon icon="lucide:flask-conical" className="h-8 w-8 text-[#9CA3AF]" />}
          title="No result values linked"
          description="This report doesn't have individual component values attached."
        />
      )}

      <div className="mt-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
        {result.results.map((r, i) => {
          const meta = getResultStatusMeta(r.status);
          return (
            <div key={i} className="rounded-2xl border border-[#E5E7EB] bg-white p-4">
              <div className="flex items-center justify-between gap-2">
                <p className="text-xs font-bold text-[#111827]">{r.componentName}</p>
                <span className="text-[10px] font-bold text-[#111827]">{meta.label}</span>
              </div>
              <div className="mt-2 flex items-baseline gap-1">
                <span className={cn("text-2xl font-black", meta.valueClass)}>{r.value || "—"}</span>
                <span className="text-xs font-medium text-[#111827]">{r.unit}</span>
              </div>
              <span className={cn("mt-1.5 inline-block h-1.5 w-1.5 rounded-full", meta.dotClass)} />
              <p className="mt-1 text-[10px] text-[#9CA3AF]">
                {r.referenceRange
                  ? r.unit ? `${r.referenceRange} ${r.unit}` : r.referenceRange
                  : "Range not available"}
              </p>
            </div>
          );
        })}
      </div>

      {(result.diagnosticReportId || result.documentReferenceId) && (
        <div className="mt-6">
          <FilePane
            diagnosticReportId={result.diagnosticReportId || undefined}
            documentReferenceId={result.documentReferenceId || undefined}
          />
        </div>
      )}
    </div>
  );
}

export function DiagnosticsImagingSection({ labResults, isLoading = false }: DiagnosticsImagingSectionProps) {
  const [activeTab, setActiveTab] = useState<Tab>("labs");
  // One shared value instead of two independent booleans/strings — the filter
  // pills ("All", each category/type, "Abnormal/Critical Only") render as a
  // single row and are meant to be mutually exclusive, but used to be backed
  // by filterCategory (string | null) and filterFlaggedOnly (boolean) as two
  // separate pieces of state. "All"'s active style only ever checked
  // !filterCategory, so it stayed lit up even while filterFlaggedOnly was
  // true — clicking "Critical Only" never touched filterCategory, so both
  // pills showed active at once. A single value makes "select one" the only
  // representable state.
  const [activeFilter, setActiveFilter] = useState<string>("all");
  const filterCategory = activeFilter === "all" || activeFilter === "flagged" ? null : activeFilter;
  const filterFlaggedOnly = activeFilter === "flagged";
  const [selectedLabId, setSelectedLabId] = useState<string | null>(labResults[0]?.id ?? null);

  // Patient-uploaded DICOM studies — sourced from ImagingStudiesContext
  // (mounted once in PatientProtectedLayout.tsx) so this data survives
  // navigating away from and back to this page. Uploading now happens from
  // the Upload Records page (see UploadRecords.tsx) — this page is
  // browse/view-only, just the list and the viewer.
  //
  // Deliberately NOT reading `activeBatches` here: this tab is explicitly
  // browse/view-only (no upload trigger of its own, per this section's own
  // docstring below), and ImagingStudyList's row shape is built entirely
  // around a COMPLETED series' known metadata (modality, file/frame counts)
  // that an in-flight batch simply doesn't have yet — there's no natural
  // row to show here without inventing a second, differently-shaped item
  // type for a list that's otherwise uniform. The in-flight state surfaces
  // instead on the Upload Records page (RecentDicomUploadsCard), which is
  // where the patient actually is at the moment a batch gets submitted and
  // where the old in-modal progress UI's replacement belongs.
  const {
    series: uploadedSeries,
    isLoading: studiesLoading,
    isLoadingMore: studiesLoadingMore,
    hasMore: studiesHasMore,
    error: studiesError,
    totalCount: studiesTotalCount,
    loadMore: loadMoreStudies,
  } = useImagingStudies();
  const navigate = useNavigate();
  // Navigates to the DICOM viewer page (see router.tsx and
  // DicomViewerPage.tsx) instead of opening a modal — passes the
  // already-fetched series via router state so the page can render
  // immediately instead of waiting on a redundant fetch-by-id. The route
  // param itself is the series' first member's id (see
  // RecentImagingUploadsCard's own openViewer for why).
  const openViewer = (series: ImagingSeries) => {
    if (series.files.length === 0) return;
    navigate(`/patient/diagnostics/imaging/${series.files[0].id}`, { state: { series } });
  };

  const totalLabTests = labResults.length;
  const abnormalLabTests = labResults.filter(r => r.results.some(res => isAbnormalLabStatus(res.status))).length;
  const criticalFlags = labResults.filter(r => r.criticalFlags).length;

  const categories = useMemo(() => [...new Set(labResults.map(r => r.category))], [labResults]);

  const filteredLabResults = labResults.filter(result => {
    const matchesCategory = !filterCategory || result.category === filterCategory;
    const matchesFlagged = !filterFlaggedOnly || result.results.some(r => isAbnormalLabStatus(r.status));
    return matchesCategory && matchesFlagged;
  });

  const selectedLabResult = filteredLabResults.find(r => r.id === selectedLabId) ?? filteredLabResults[0] ?? null;

  // Whether the Lab Results tab's underlying dataset has anything in it at
  // all — as opposed to the current filter simply matching nothing. The
  // filter pills are meaningless with nothing to filter, so they're hidden
  // entirely in that case instead of sitting above an empty list. The
  // Imaging tab has no equivalent: the upload control is always shown
  // regardless of whether any studies exist yet.
  const hasAnyLabData = totalLabTests > 0;

  const handleTabChange = (tab: Tab) => {
    setActiveTab(tab);
    setActiveFilter("all");
  };

  if (isLoading) {
    return (
      <div className="min-h-full bg-[#F7F7F7] px-4 py-4 lg:px-6 lg:py-5">
        <div className="mb-5 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="min-h-[110px] rounded-3xl border border-[#E5E7EB] bg-white px-6 py-5 shadow-sm">
              <Skeleton active paragraph={{ rows: 1 }} />
            </div>
          ))}
        </div>
        <div className="grid items-start gap-5 lg:grid-cols-[340px_1fr]">
          {/* Sidebar — mirrors the real layout below (filter pills, then
              rows) instead of skipping straight to the rows, so nothing pops
              in abruptly once loading finishes. */}
          <div className="min-w-0">
            <div className="mb-4 flex flex-wrap gap-2">
              {[0, 1, 2, 3].map((i) => (
                <Skeleton.Button key={i} active size="small" shape="round" style={{ width: 76 }} />
              ))}
            </div>
            <div className="grid gap-4">
              {[0, 1, 2].map((i) => (
                <div key={i} className="rounded-2xl border border-[#E5E7EB] bg-white p-4 shadow-sm">
                  <Skeleton active title={{ width: "70%" }} paragraph={{ rows: 1, width: "40%" }} />
                </div>
              ))}
            </div>
          </div>
          <div className="rounded-3xl border border-[#E5E7EB] bg-white p-6 shadow-sm">
            <Skeleton active paragraph={{ rows: 6 }} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-[#F7F7F7] px-4 py-4 lg:px-6 lg:py-5">
      {/* Stat cards */}
      <div className="mb-5 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard icon="lucide:test-tube" label="Lab Results" value={totalLabTests} onClick={() => handleTabChange("labs")} />
        <StatCard icon="lucide:archive-x" label="Abnormal Labs" value={abnormalLabTests} onClick={() => handleTabChange("labs")} />
        <StatCard icon="lucide:scan-line" label="Imaging Studies" value={studiesTotalCount} onClick={() => handleTabChange("imaging")} />
        <StatCard icon="lucide:flag" label="Critical Labs" value={criticalFlags} onClick={() => handleTabChange("labs")} />
      </div>

      {/* Tab toggle */}
      <div className="mb-6 flex justify-center">
        <Segmented
          value={activeTab}
          onChange={(v) => handleTabChange(v as Tab)}
          options={[
            { key: "labs" as const, icon: "lucide:test-tube", label: "Lab Results" },
            { key: "imaging" as const, icon: "lucide:scan-line", label: "Imaging" },
          ].map(({ key, icon, label }) => ({
            value: key,
            label: (
              <span className="flex items-center gap-2 px-5 py-1 text-sm font-extrabold">
                <Icon icon={icon} className="h-4 w-4" />
                {label}
              </span>
            ),
          }))}
          className="!rounded-full !border !border-[#180047]/30 !bg-white !p-1 !shadow-sm [&_.ant-segmented-item]:!rounded-full [&_.ant-segmented-item-selected]:!bg-[#180047] [&_.ant-segmented-item-selected]:!shadow-sm [&_.ant-segmented-item-selected_span]:!text-white [&_.ant-segmented-item:not(.ant-segmented-item-selected)_span]:!text-[#180047] [&_.ant-segmented-thumb]:!rounded-full"
        />
      </div>

      {activeTab === "labs" ? (
        /* Nothing to show at all — one full-width empty state instead of the
           split view. With no data, a search box and filter pills have
           nothing to act on, and a second "select an item" card next to an
           already-empty list is redundant, so both are skipped. */
        !hasAnyLabData ? (
          <EmptyState
            icon={<Icon icon="lucide:test-tube" className="h-8 w-8 text-[#9CA3AF]" />}
            title="No lab results yet"
            description="Your lab results will appear here once they're available."
            className="py-24"
          />
        ) : (
          /* Split view */
          <div className="grid items-start gap-5 lg:grid-cols-[340px_1fr]">
            {/* Sidebar — min-w-0 stops this grid item (and the single-column list
                grid inside it) from growing past its 340px track when a row's
                content is wide, which otherwise overflows into the detail panel
                next to it instead of respecting the split. */}
            <div className="min-w-0">
              <div className="mb-4 flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  onClick={() => setActiveFilter("all")}
                  className={cn(filterPillClass, activeFilter === "all" ? filterPillActiveClass : filterPillInactiveClass)}
                >
                  All
                </Button>
                {categories.map((c) => (
                  <Button
                    key={c}
                    variant="outline"
                    onClick={() => setActiveFilter(activeFilter === c ? "all" : c)}
                    className={cn(filterPillClass, activeFilter === c ? filterPillActiveClass : filterPillInactiveClass)}
                  >
                    {categoryLabels[c]}
                  </Button>
                ))}
                <Button
                  variant="outline"
                  onClick={() => setActiveFilter(activeFilter === "flagged" ? "all" : "flagged")}
                  className={cn(filterPillClass, activeFilter === "flagged" ? filterPillActiveClass : filterPillInactiveClass)}
                >
                  Abnormal Only
                </Button>
              </div>

              <div className="grid gap-4">
                {filteredLabResults.length === 0 ? (
                  <EmptyListState icon="lucide:test-tube" message="No lab results match this filter" />
                ) : (
                  filteredLabResults.map((r) => (
                    <LabRow key={r.id} result={r} isSelected={r.id === selectedLabResult?.id} onSelect={() => setSelectedLabId(r.id)} />
                  ))
                )}
              </div>
            </div>

            {/* Detail panel — sticky so it stays in view while scrolling a long
                lab-results list next to it, instead of scrolling away above the
                fold (matches the pattern in FhirExportView's "Export
                Summary"). Renders nothing when the list is filtered down to
                zero — there's nothing to select, so the sidebar's own empty
                message is the only thing on screen instead of a second one
                sitting next to it. */}
            <div className="sticky top-5">
              {selectedLabResult
                ? <LabResultDetail result={selectedLabResult} />
                : (filteredLabResults.length > 0 && <EmptyListState icon="lucide:test-tube" message="Select a lab result to view details" />)}
            </div>
          </div>
        )
      ) : (
        /* Imaging tab — entirely patient-uploaded DICOM studies now (no EHR/
           data-feed imaging is surfaced here any more). Browse/view only —
           uploading happens from the Upload Records page (see
           UploadRecords.tsx), not here. */
        <div className="rounded-xl border border-[#E5E7EB] bg-white p-6 shadow-sm">
          <h3 className="text-sm font-extrabold text-[#0A2947]">Imaging</h3>
          <div className="mt-4">
            {studiesLoading ? (
              <div className="grid gap-3">
                {[0, 1, 2].map((i) => (
                  <div key={i} className="rounded-2xl border border-[#E5E7EB] bg-white p-4 shadow-sm">
                    <Skeleton active title={{ width: "50%" }} paragraph={{ rows: 1, width: "30%" }} />
                  </div>
                ))}
              </div>
            ) : studiesError ? (
              <EmptyState
                icon={<Icon icon="lucide:wifi-off" className="h-8 w-8 text-[#9CA3AF]" />}
                title="Couldn't load your imaging studies"
                description={studiesError}
                className="py-16"
              />
            ) : uploadedSeries.length === 0 ? (
              <EmptyState
                icon={<Icon icon="lucide:scan-line" className="h-8 w-8 text-[#9CA3AF]" />}
                title="No images found to show"
                description="Upload a DICOM file from the Upload Records page to see it here."
                className="py-16"
                action={
                  <Button
                    onClick={() => navigate("/patient/upload")}
                    className="!h-auto !rounded-xl !bg-[#E4002B]/50 !px-5 !py-2.5 !text-sm !font-extrabold !text-white hover:!bg-[#E4002B]/60"
                  >
                    Go to Upload Records
                  </Button>
                }
              />
            ) : (
              <ImagingStudyList
                series={uploadedSeries}
                onSelect={openViewer}
                hasMore={studiesHasMore}
                isLoadingMore={studiesLoadingMore}
                onLoadMore={loadMoreStudies}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}
